/**
 * dcg-cart-enforcer.js — Desert City Games  v3
 *
 * Cart quantity enforcement engine. Enforces both limit:N product tags
 * and Shopify inventory stock counts across all add-to-cart paths and
 * on cart-page load.
 *
 * §1  Variant Registry   — maps variantId → { tags[], stockCap }
 * §2  Limit parsing      — parseLimitFromTags / parseLimitFromTagString
 * §3  Cart API helpers   — /cart.js fetch, /cart/change.js update
 * §4  ATC pre-flight     — called by dcg-cart-bridge.js before /cart/add.js
 * §5  Cart page audit    — auto-corrects over-limit qtys on .template-cart load
 * §6  Cart drawer audit  — best-effort correction after cart:updated events
 * §7  Message UI         — toast (global) + banner (cart page only)
 *
 * v3 changes (fixes advanced search enforcement):
 *
 *   ROOT CAUSE 1 — MutationObserver never caught Phase 2 data.
 *   Advanced search cards are built in two phases by advanced_search.liquid:
 *     Phase 1 (reshapeCard): card injected into DOM, NO data-product-tags yet.
 *     Phase 2 (enrichCard):  async /products/{handle}.js fetch completes, THEN
 *                            data-product-tags, data-product-variants, and
 *                            data-variant-inventory are stamped as dataset attrs.
 *                            Phase 2 then calls dcgInitProductCards which calls
 *                            buildCardUI() which modifies the card's inner DOM.
 *   The v2 observer watched {childList:true, subtree:true}. It fired during
 *   Phase 1 (wrong — no attrs yet) and during buildCardUI (correct timing,
 *   wrong elements: addedNodes were child divs, not the attr-bearing wrapper).
 *   Fix: observer now walks UP from each addedNode to find the wrapper.
 *
 *   ROOT CAUSE 2 — Only limit tags were enforced, not stock counts.
 *   The failing test product has 6 actual inventory but no limit:N tag.
 *   Preflight found no limit → passed through → 3 + 6 = 9 in cart.
 *   Fix: registry now stores stockCap from data-variant-inventory alongside
 *   tags. preflight combines both into effectiveMax = min(limit, stockCap).
 *
 * Load order: this file is in <head> BEFORE dcg-cart-bridge.js (body/section).
 * Both use defer so <head> scripts execute first per DOM order.
 *
 * Avada Order Limit coexistence: Avada enforces at Shopify checkout level;
 * we enforce at cart-API level. No shared DOM, no shared events.
 *
 * MTG safety: issues only /cart.js and /cart/change.js — never touches
 * BinderPOS or Shopify Storefront API paths.
 */

window.DCG = window.DCG || {};

window.DCG.enforcer = (function () {
  'use strict';

  /* ═══════════════════════════════════════════════════════════════════════
     §1  VARIANT REGISTRY
     Maps String(variantId) → { tags: string[], stockCap: number|null }
       tags     — product tag array (for limit:N enforcement)
       stockCap — max purchasable stock, or null if unconstrained.
                  Only set for Shopify-managed inventory with deny policy.

     Populated by buildRegistry() at DOMContentLoaded and by the
     MutationObserver as cards are enriched by Phase 2 (enrichCard).
     ═══════════════════════════════════════════════════════════════════════ */

  var _registry = {};
  var _observerInstalled = false;

  function registerVariant(variantId, tags, stockCap) {
    if (!variantId) return;
    var key = String(variantId);
    var existing = _registry[key];

    // Merge rule: prefer richer data. Don't overwrite a known stockCap with null,
    // and don't overwrite a longer tag list with a shorter one.
    var newTags = Array.isArray(tags) && tags.length ? tags : (existing ? existing.tags : []);
    var newStock = (typeof stockCap === 'number' && stockCap > 0)
      ? stockCap
      : (existing ? existing.stockCap : null);

    if (!existing || newTags.length >= existing.tags.length || newStock !== existing.stockCap) {
      _registry[key] = { tags: newTags, stockCap: newStock };
    }
  }

  function getVariantInfo(variantId) {
    return _registry[String(variantId)] || null;
  }

  /* ═══════════════════════════════════════════════════════════════════════
     §2  LIMIT PARSING
     Handles:  limit-1  limit-one…limit-ten  limit:1
     Returns the lowest numeric limit found across all tags, or null.
     ═══════════════════════════════════════════════════════════════════════ */

  var WORD_TO_NUM = {
    one: 1, two: 2, three: 3, four: 4, five: 5,
    six: 6, seven: 7, eight: 8, nine: 9, ten: 10
  };

  function parseLimitFromTags(tags) {
    if (!Array.isArray(tags) || !tags.length) return null;
    // Prefer the shared parser (dcg-utils.js) — single source of truth.
    if (window.DCG && typeof window.DCG.parseLimit === 'function') {
      return window.DCG.parseLimit(tags);
    }
    var limits = [];
    for (var i = 0; i < tags.length; i++) {
      var tag = String(tags[i] || '').trim().toLowerCase();
      var m = tag.match(/^limit[-:](\d{1,3})$/);
      if (m) { var n = parseInt(m[1], 10); if (n > 0) limits.push(n); continue; }
      m = tag.match(/^limit-(one|two|three|four|five|six|seven|eight|nine|ten)$/);
      if (m) { var w = WORD_TO_NUM[m[1]]; if (w) limits.push(w); }
    }
    return limits.length ? Math.min.apply(null, limits) : null;
  }

  function parseLimitFromTagString(tagsAttr) {
    if (!tagsAttr) return null;
    return parseLimitFromTags(
      tagsAttr.split(',').map(function (t) { return t.trim(); }).filter(Boolean)
    );
  }

  /* ═══════════════════════════════════════════════════════════════════════
     §3  CART API HELPERS
     ═══════════════════════════════════════════════════════════════════════ */

  function fetchCart() {
    return fetch('/cart.js', {
      method: 'GET',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    }).then(function (res) {
      if (!res.ok) throw new Error('[DCG enforcer] /cart.js ' + res.status);
      return res.json();
    });
  }

  function getCartQtyForVariant(cart, variantId) {
    var id = Number(variantId);
    var total = 0;
    if (!cart || !Array.isArray(cart.items)) return 0;
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].variant_id === id) total += (cart.items[i].quantity || 0);
    }
    return total;
  }

  function updateCartVariantQty(variantId, newQty) {
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: Number(variantId), quantity: Number(newQty) })
    }).then(function (res) {
      if (!res.ok) throw new Error('[DCG enforcer] /cart/change.js ' + res.status);
      return res.json();
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     §4  ATC PRE-FLIGHT
     Called by dcg-cart-bridge.js BEFORE every /cart/add.js request.
     Returns a Promise resolving to: { qty, blocked, message }

     Enforcement sources (in priority order):
       1. Registry (pre-populated at DOMContentLoaded + observer)
       2. Live DOM scan (catches cards missed by observer timing)
     effectiveMax = min(limitTag, stockCap)
     If neither is known, the variant passes through safely (fail open).
     The cart page audit (§5) is always the guaranteed final backstop.
     ═══════════════════════════════════════════════════════════════════════ */

  function preflight(variantId, requestedQty) {
    var vid = Number(variantId);
    var qty = Number(requestedQty) || 1;

    if (!vid || qty <= 0) {
      return Promise.resolve({ qty: qty, blocked: false, message: null });
    }

    // ── Step 1: registry lookup ────────────────────────────────────────────
    var info = getVariantInfo(vid);

    // ── Step 2: live DOM scan if registry missed ───────────────────────────
    // After Phase 2 (enrichCard) completes, data-product-tags,
    // data-product-variants, and data-variant-inventory are all on the wrapper.
    // The observer walk-up should have caught this, but we scan as a fallback
    // to handle any timing gaps between Phase 2 completion and observer firing.
    if (!info) {
      var scanned = _scanDOMForVariant(vid);
      if (scanned) {
        registerVariant(vid, scanned.tags, scanned.stockCap);
        info = getVariantInfo(vid);
      }
    }

    // ── Step 3: compute effective max ─────────────────────────────────────
    var limit    = info ? parseLimitFromTags(info.tags) : null;
    var stockCap = info ? info.stockCap : null;

    var effectiveMax = Math.min(
      limit    !== null ? limit    : Infinity,
      stockCap !== null ? stockCap : Infinity
    );

    // No enforceable constraint found — pass through immediately (no fetch).
    // Cart page audit will still correct any overage before checkout.
    if (!isFinite(effectiveMax)) {
      return Promise.resolve({ qty: qty, blocked: false, message: null });
    }

    // ── Step 4: cross-check current cart ──────────────────────────────────
    return fetchCart().then(function (cart) {
      var inCart = getCartQtyForVariant(cart, vid);

      // Build a clear reason string for user messages
      var reasonFull  = _buildReason(limit, stockCap, effectiveMax, 'full');
      var reasonShort = _buildReason(limit, stockCap, effectiveMax, 'short');

      if (inCart >= effectiveMax) {
        return {
          qty: 0,
          blocked: true,
          message: 'You already have ' + inCart + ' of this item in your cart ('
            + reasonFull + ').'
        };
      }

      var allowable = effectiveMax - inCart;

      if (qty > allowable) {
        return {
          qty: allowable,
          blocked: false,
          message: 'Quantity reduced to ' + allowable + ' \u2014 ' + reasonShort + '.'
        };
      }

      return { qty: qty, blocked: false, message: null };

    }).catch(function (err) {
      console.warn('[DCG enforcer] preflight cart fetch failed \u2014 allowing add:', err);
      return { qty: qty, blocked: false, message: null };
    });
  }

  // Produce a human-readable constraint reason for toast messages.
  // mode='full'  → "limit: 6 per customer" / "only 6 available" / "max: 6"
  // mode='short' → "limit is 6 per customer" / "6 remaining in stock"
  function _buildReason(limit, stockCap, effectiveMax, mode) {
    var byLimit = limit !== null && (stockCap === null || limit <= stockCap);
    if (byLimit) {
      return mode === 'full'
        ? 'limit: ' + effectiveMax + ' per customer'
        : 'limit is ' + effectiveMax + ' per customer';
    }
    if (stockCap !== null && (limit === null || stockCap < limit)) {
      return mode === 'full'
        ? 'only ' + effectiveMax + ' available'
        : effectiveMax + ' remaining in stock';
    }
    return mode === 'full'
      ? 'max: ' + effectiveMax
      : 'max is ' + effectiveMax;
  }

  /* ── Live DOM scan ─────────────────────────────────────────────────────
     Searches the current document for a card wrapper or product section
     containing this variantId. Returns { tags, stockCap } or null.

     Called by preflight() when the variant is absent from the registry —
     this catches any cards that Phase 2 enriched before the observer walked
     up, or cases where the user clicked ATC in the gap between Phase 2 and
     the observer callback firing.
     ────────────────────────────────────────────────────────────────────── */
  function _scanDOMForVariant(variantId) {
    var vid = String(variantId);

    // Source A: product page section (data-dcg-tags + ProductSelect)
    var sections = document.querySelectorAll(
      '[data-section-type="product"], [data-section-type="product-template"]'
    );
    for (var s = 0; s < sections.length; s++) {
      var masterSelect = sections[s].querySelector('select[id^="ProductSelect-"]');
      if (!masterSelect) continue;
      var opts = masterSelect.options;
      for (var o = 0; o < opts.length; o++) {
        if (String(opts[o].value) === vid) {
          var tagsAttr = sections[s].getAttribute('data-dcg-tags') || '';
          var tags = tagsAttr.split(',').map(function (t) { return t.trim(); }).filter(Boolean);
          // Product-page stock: read the SAME data-variant-inventory map the
          // collection cards emit, and apply the IDENTICAL cap logic used in
          // Source B below — Shopify-managed + deny policy + qty > 0 → that qty
          // is the stock cap. This makes cumulative add-to-cart on the product
          // page enforce stock exactly like the collection page (dcg-qty-guard
          // only caps the qty INPUT, not repeated single-qty ATC clicks). A
          // 'continue' policy or qty 0 (untracked / BinderPOS-unsynced) stays
          // uncapped — same as the cards.
          var pdpStockCap = null;
          var invAttr = sections[s].getAttribute('data-variant-inventory');
          if (invAttr) {
            // Preferred: the SAME per-variant map the collection cards emit,
            // with identical logic (Source B) — Shopify-managed + deny + qty>0.
            // A 'continue' policy or qty 0 stays uncapped, matching the cards.
            try {
              var invMap = JSON.parse(invAttr);
              var inv = invMap[vid] || invMap[Number(vid)];
              if (inv && inv.management === 'shopify' && inv.policy === 'deny' && inv.qty > 0) {
                pdpStockCap = inv.qty;
              }
            } catch (e) {}
          } else {
            // Fallback when the section has no inventory map (older template):
            // the master <select> option carries data-stock (Shopify
            // inventory_quantity), already present on every product page.
            // Positive → cap at on-hand stock.
            var ds = parseInt(opts[o].getAttribute('data-stock'), 10);
            if (isFinite(ds) && ds > 0) pdpStockCap = ds;
          }
          if (tags.length || pdpStockCap !== null) {
            return { tags: tags, stockCap: pdpStockCap };
          }
          return null;
        }
      }
    }

    // Source B: product card wrappers (data-product-tags, set by Phase 2 enrichCard)
    // Note: data-product-tags requires Phase 2 to have completed. The observer
    // walk-up handles most cases; this scan is the timing-gap fallback.
    var cards = document.querySelectorAll('[data-product-tags]');
    for (var c = 0; c < cards.length; c++) {
      var card = cards[c];
      try {
        // Check if this card contains our variant
        var variantsAttr = card.getAttribute('data-product-variants');
        if (!variantsAttr) continue;

        var variants = JSON.parse(variantsAttr);
        if (!Array.isArray(variants)) continue;

        var hasVariant = variants.some(function (v) {
          return v && String(v.id) === vid;
        });
        if (!hasVariant) continue;

        // Extract tags
        var cardTags = JSON.parse(card.getAttribute('data-product-tags') || '[]');

        // Extract stock cap from data-variant-inventory (set by enrichCard)
        var stockCap = null;
        var invAttr = card.getAttribute('data-variant-inventory');
        if (invAttr) {
          var invMap = JSON.parse(invAttr);
          // Keys in JSON-serialized object are strings
          var inv = invMap[vid] || invMap[Number(vid)];
          if (inv && inv.management === 'shopify' && inv.policy === 'deny' && inv.qty > 0) {
            stockCap = inv.qty;
          }
        }

        return {
          tags:     Array.isArray(cardTags) ? cardTags : [],
          stockCap: stockCap
        };

      } catch (e) {
        // Malformed JSON on this card — skip, try others
      }
    }

    return null;
  }

  /* ═══════════════════════════════════════════════════════════════════════
     §5  CART PAGE ON-LOAD AUDIT
     Runs once when .template-cart is present. Reads each qty input's
     data-tags and max attributes, computes effective max, and corrects
     any qty via /cart/change.js. Shows a persistent banner.

     This is the guaranteed enforcement backstop — it runs regardless of
     whether the ATC preflight caught the violation, and it handles cases
     where the customer navigates directly to /cart with an over-limit cart.
     ═══════════════════════════════════════════════════════════════════════ */

  function auditCartPage() {
    if (!document.querySelector('.template-cart')) return;

    var inputs = document.querySelectorAll(
      '.template-cart input.cart__qty-input[data-variant-id]'
    );
    if (!inputs.length) return;

    var corrections = [];

    for (var i = 0; i < inputs.length; i++) {
      var inp = inputs[i];
      var variantId = inp.getAttribute('data-variant-id');
      var currentVal = parseInt(inp.value, 10);

      if (!variantId || isNaN(currentVal) || currentVal <= 0) continue;

      // Source A: limit-N product tags (always in data-tags, set by Liquid)
      var limit = parseLimitFromTagString(inp.getAttribute('data-tags') || '');

      // Source B: Shopify stock cap (max attr — set only for Shopify-managed
      // inventory with deny policy; blank for BinderPOS-managed products)
      var stockCap = Infinity;
      var maxAttr = inp.getAttribute('max');
      if (maxAttr) {
        var parsedMax = parseInt(maxAttr, 10);
        if (!isNaN(parsedMax) && parsedMax > 0) stockCap = parsedMax;
      }

      var effectiveMax = Math.min(
        limit !== null ? limit : Infinity,
        stockCap
      );

      if (!isFinite(effectiveMax) || effectiveMax <= 0) continue;
      if (currentVal <= effectiveMax) continue;

      corrections.push({
        inp:         inp,
        variantId:   variantId,
        correctTo:   effectiveMax,
        limit:       limit,
        stockCap:    isFinite(stockCap) ? stockCap : null,
        originalVal: currentVal
      });
    }

    if (!corrections.length) return;

    var messages = [];
    var chain = Promise.resolve();

    corrections.forEach(function (c) {
      chain = chain.then(function () {
        return updateCartVariantQty(c.variantId, c.correctTo)
          .then(function () {
            c.inp.value = String(c.correctTo);

            var stepper = _closestByClass(c.inp, 'cart-qty-btns');
            if (stepper) {
              var plus = stepper.querySelector('.inc, .qtyplus');
              if (plus) {
                var atMax = c.correctTo >= c.correctTo; // always true at effectiveMax
                plus.classList.add('qty-at-max');
                plus.setAttribute('aria-disabled', 'true');
              }
            }

            if (c.correctTo === 0) {
              messages.push('An item was removed from your cart (purchase limit exceeded).');
            } else if (c.stockCap !== null && (c.limit === null || c.stockCap <= c.limit)) {
              messages.push(
                'Quantity adjusted to ' + c.correctTo
                + ' (only ' + c.stockCap + ' available).'
              );
            } else {
              messages.push(
                'Quantity adjusted to ' + c.correctTo
                + ' (limit: ' + c.limit + ' per customer).'
              );
            }
          })
          .catch(function (err) {
            console.warn('[DCG enforcer] cart/change.js failed for variant', c.variantId, err);
          });
      });
    });

    chain.then(function () {
      if (!messages.length) return;
      showCartBanner(messages.join(' '));
      if (typeof window.adjustCartDropDown === 'function') {
        window.adjustCartDropDown();
      } else {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     §6  CART DRAWER AUDIT (best-effort)
     ═══════════════════════════════════════════════════════════════════════ */

  var _drawerAuditPending = false;

  function scheduleDrawerAudit() {
    if (_drawerAuditPending) return;
    _drawerAuditPending = true;
    setTimeout(function () {
      _drawerAuditPending = false;
      runDrawerAudit();
    }, 500);
  }

  function runDrawerAudit() {
    // Only run if at least one variant in the registry has an enforceable constraint
    var hasConstraints = Object.keys(_registry).some(function (vid) {
      var info = _registry[vid];
      return parseLimitFromTags(info.tags) !== null || info.stockCap !== null;
    });
    if (!hasConstraints) return;

    fetchCart().then(function (cart) {
      if (!cart || !Array.isArray(cart.items) || !cart.items.length) return;

      var violations = [];
      var seenVariantIds = {};

      cart.items.forEach(function (item) {
        var vid = item.variant_id;
        if (seenVariantIds[vid]) return;

        var info = getVariantInfo(vid);
        if (!info) return;

        var limit    = parseLimitFromTags(info.tags);
        var stockCap = info.stockCap;
        var effectiveMax = Math.min(
          limit    !== null ? limit    : Infinity,
          stockCap !== null ? stockCap : Infinity
        );

        if (!isFinite(effectiveMax)) return;

        var totalQty = getCartQtyForVariant(cart, vid);
        if (totalQty <= effectiveMax) return;

        seenVariantIds[vid] = true;
        violations.push({
          variantId:    vid,
          title:        item.title,
          correctTo:    effectiveMax,
          limit:        limit,
          stockCap:     stockCap
        });
      });

      if (!violations.length) return;

      var messages = [];
      var chain = Promise.resolve();

      violations.forEach(function (v) {
        chain = chain.then(function () {
          return updateCartVariantQty(v.variantId, v.correctTo)
            .then(function () {
              var reason = v.stockCap !== null && (v.limit === null || v.stockCap <= v.limit)
                ? 'only ' + v.stockCap + ' available'
                : 'limit: ' + v.limit + ' per customer';
              messages.push(
                '\u201c' + v.title + '\u201d adjusted to ' + v.correctTo
                + ' (' + reason + ').'
              );
            })
            .catch(function (err) {
              console.warn('[DCG enforcer] drawer audit correction failed:', err);
            });
        });
      });

      chain.then(function () {
        if (!messages.length) return;
        showToast('\u26a0\ufe0f Cart updated: ' + messages.join(' '));
        if (typeof window.adjustCartDropDown === 'function') {
          window.adjustCartDropDown();
        }
      });

    }).catch(function (err) {
      console.warn('[DCG enforcer] drawer audit /cart.js fetch failed:', err);
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     §7  MESSAGE UI
     ═══════════════════════════════════════════════════════════════════════ */

  function showToast(message) {
    var toast = document.getElementById('dcg-enforcer-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'dcg-enforcer-toast';
      toast.setAttribute('role', 'alert');
      toast.setAttribute('aria-live', 'assertive');
      toast.style.cssText = [
        'position:fixed',
        'top:20px',
        'left:50%',
        'transform:translateX(-50%)',
        'z-index:10000',
        'background:var(--dcg-orange,#FF7A00)',
        'color:#fff',
        'padding:11px 22px',
        'border-radius:6px',
        'font-size:13px',
        'font-weight:600',
        'line-height:1.45',
        'box-shadow:0 4px 20px rgba(0,0,0,.3)',
        'max-width:90vw',
        'width:420px',
        'text-align:center',
        'pointer-events:none',
        'transition:opacity .3s ease',
        'display:none',
        'opacity:0'
      ].join(';');
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.style.display = 'block';
    void toast.offsetHeight;
    toast.style.opacity = '1';
    clearTimeout(toast._dcgTimer);
    toast._dcgTimer = setTimeout(function () {
      toast.style.opacity = '0';
      setTimeout(function () { if (toast.parentNode) toast.style.display = 'none'; }, 320);
    }, 5000);
  }

  function showCartBanner(message) {
    var existing = document.getElementById('dcg-enforcer-banner');
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);

    var banner = document.createElement('div');
    banner.id = 'dcg-enforcer-banner';
    banner.setAttribute('role', 'alert');
    banner.setAttribute('aria-live', 'polite');
    banner.style.cssText = [
      'background:var(--dcg-orange,#FF7A00)',
      'color:#fff',
      'padding:13px 20px',
      'border-radius:6px',
      'font-size:13px',
      'font-weight:600',
      'line-height:1.5',
      'margin-bottom:18px',
      'text-align:center'
    ].join(';');
    banner.textContent = '\u26a0\ufe0f ' + message;

    var form = document.querySelector('.template-cart form[action="/cart"]');
    if (form && form.parentNode) {
      form.parentNode.insertBefore(banner, form);
    } else {
      var cartEl = document.querySelector('.template-cart');
      if (cartEl) cartEl.insertBefore(banner, cartEl.firstChild);
    }
  }

  /* ═══════════════════════════════════════════════════════════════════════
     REGISTRY BUILDER + MUTATIONOBSERVER
     ═══════════════════════════════════════════════════════════════════════ */

  function buildRegistry() {
    // Source 1: product page sections
    var sections = document.querySelectorAll(
      '[data-section-type="product"], [data-section-type="product-template"]'
    );
    for (var s = 0; s < sections.length; s++) {
      var tagsAttr = sections[s].getAttribute('data-dcg-tags') || '';
      var tags = tagsAttr.split(',').map(function (t) { return t.trim(); }).filter(Boolean);
      if (!tags.length) continue;
      var ms = sections[s].querySelector('select[id^="ProductSelect-"]');
      if (!ms) continue;
      // Register product-page variants WITH their stock cap — read it the SAME
      // way the collection cards do (_registerCardEl): prefer the
      // data-variant-inventory map (Shopify-managed + deny policy + qty>0), else
      // fall back to each option's data-stock (Shopify inventory_quantity, which
      // is already present on every product page). These were previously
      // registered with a NULL cap, which shadowed every stock lookup and let
      // add-to-cart exceed on-hand stock on the product page.
      var secInvMap = {};
      var secInvAttr = sections[s].getAttribute('data-variant-inventory');
      if (secInvAttr) { try { secInvMap = JSON.parse(secInvAttr); } catch (e) { secInvMap = {}; } }
      for (var o = 0; o < ms.options.length; o++) {
        var optVid = ms.options[o].value;
        if (!optVid) continue;
        var optStock = null;
        var invEntry = secInvMap[optVid] || secInvMap[Number(optVid)];
        if (invEntry && invEntry.management === 'shopify' && invEntry.policy === 'deny' && invEntry.qty > 0) {
          optStock = invEntry.qty;
        } else {
          var ods = parseInt(ms.options[o].getAttribute('data-stock'), 10);
          if (isFinite(ods) && ods > 0) optStock = ods;
        }
        registerVariant(optVid, tags, optStock);
      }
    }

    // Source 2: product card wrappers (set by Phase 2 enrichCard)
    var cards = document.querySelectorAll('[data-product-tags][data-product-variants]');
    for (var c = 0; c < cards.length; c++) {
      _registerCardEl(cards[c]);
    }

    // Source 3: cart page inputs
    var cartInputs = document.querySelectorAll('input[data-variant-id][data-tags]');
    for (var i = 0; i < cartInputs.length; i++) {
      var vid = cartInputs[i].getAttribute('data-variant-id');
      var inputTags = (cartInputs[i].getAttribute('data-tags') || '')
        .split(',').map(function (t) { return t.trim(); }).filter(Boolean);
      // Stock from max attr (Shopify-managed only)
      var inputStock = null;
      var ma = cartInputs[i].getAttribute('max');
      if (ma) { var mp = parseInt(ma, 10); if (!isNaN(mp) && mp > 0) inputStock = mp; }
      if (vid) registerVariant(vid, inputTags, inputStock);
    }

    _installObserver();
  }

  // Register all variants from a single card element.
  // Reads data-product-tags (tags), data-product-variants (variant IDs),
  // and data-variant-inventory (stock counts — set by Phase 2 enrichCard).
  function _registerCardEl(el) {
    if (!el) return;
    try {
      var cardTags = JSON.parse(el.getAttribute('data-product-tags') || '[]');
      var variants = JSON.parse(el.getAttribute('data-product-variants') || '[]');
      var invMap   = {};
      var invAttr  = el.getAttribute('data-variant-inventory');
      if (invAttr) invMap = JSON.parse(invAttr);

      if (!Array.isArray(variants)) return;

      variants.forEach(function (v) {
        if (!v || !v.id) return;
        var vid = String(v.id);

        // Extract stock cap: only for Shopify-managed inventory with deny policy
        var stockCap = null;
        var inv = invMap[vid] || invMap[Number(vid)];
        if (inv && inv.management === 'shopify' && inv.policy === 'deny' && inv.qty > 0) {
          stockCap = inv.qty;
        }

        registerVariant(v.id, Array.isArray(cardTags) ? cardTags : [], stockCap);
      });
    } catch (e) {
      // Malformed JSON — skip silently
    }
  }

  // Install MutationObservers ONLY on the containers where product cards
  // appear or get hydrated. Previously this observed document.body with
  // subtree:true — every DOM mutation anywhere on the page fired the
  // handler, which on heavy collection pages (Flesh-and-Blood, MTG Singles)
  // caused tens of thousands of unnecessary walk-up traversals per card
  // render burst, pinning the main thread for minutes. Scoped observers
  // fire only when the watched container's subtree mutates — typically
  // 100× fewer callbacks on heavy pages.
  //
  // The observer callback walks UP from each addedNode to find the card
  // wrapper. This catches Phase 2 (enrichCard) completions on advanced-search:
  //   1. Phase 2 sets data-product-tags on the wrapper (attribute change —
  //      NOT caught by childList observer).
  //   2. Phase 2 calls dcgInitProductCards → buildCardUI → modifies card
  //      innerHTML (childList change — IS caught).
  //   3. The addedNodes from step 2 are inner divs (.dcg-card-top etc.).
  //      Walking up 6 levels reaches the wrapper, which now has data-product-tags.
  //   4. _registerCardEl registers all variants from that wrapper.
  function _installObserver() {
    if (_observerInstalled || typeof MutationObserver === 'undefined') return;
    _observerInstalled = true;

    var observerCallback = function (mutations) {
      for (var m = 0; m < mutations.length; m++) {
        var added = mutations[m].addedNodes;
        for (var n = 0; n < added.length; n++) {
          var node = added[n];
          if (node.nodeType !== 1) continue;

          // The added node itself is a card wrapper
          if (node.hasAttribute &&
              node.hasAttribute('data-product-tags') &&
              node.hasAttribute('data-product-variants')) {
            _registerCardEl(node);
          }

          // A container holding multiple card wrappers
          if (node.querySelectorAll) {
            var inner = node.querySelectorAll('[data-product-tags][data-product-variants]');
            for (var ic = 0; ic < inner.length; ic++) {
              _registerCardEl(inner[ic]);
            }
          }

          // Walk UP from the added node to find the card wrapper.
          var parent = node.parentElement;
          var depth  = 0;
          while (parent && depth < 6) {
            if (parent.hasAttribute && parent.hasAttribute('data-product-tags')) {
              _registerCardEl(parent);
              break;
            }
            parent = parent.parentElement;
            depth++;
          }
        }
      }
    };

    // Scope: only the containers where product cards live or get hydrated.
    // Each is checked individually — most pages have only a subset of these.
    // Pages with no card-rendering surface skip the observer entirely.
    //
    // Fallback for variants missed by the observer: preflight() in §4 does a
    // _scanDOMForVariant live lookup if the variant isn't in the registry,
    // and the cart-page audit in §5 is a final backstop. So this scope can
    // miss edge surfaces (e.g., the dcg-top-singles homepage section) without
    // breaking enforcement — those just take a slightly slower preflight path.
    var targets = [
      document.getElementById('CollectionProductGrid'),           // collection page grid
      document.getElementById('shopify-section-advanced_search'), // advanced search results
      document.querySelector('.dcg-related-products-section'),    // related products on product pages
      document.querySelector('.list-view-items.products-display') // search results page
    ].filter(Boolean);

    if (targets.length === 0) return;

    targets.forEach(function (target) {
      new MutationObserver(observerCallback)
        .observe(target, { childList: true, subtree: true });
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════
     UTILITY
     ═══════════════════════════════════════════════════════════════════════ */

  function _closestByClass(el, className) {
    if (el.closest) return el.closest('.' + className);
    var node = el.parentElement;
    while (node) {
      if (node.classList && node.classList.contains(className)) return node;
      node = node.parentElement;
    }
    return null;
  }

  /* ═══════════════════════════════════════════════════════════════════════
     INIT
     ═══════════════════════════════════════════════════════════════════════ */

  function init() {
    buildRegistry();
    if (document.querySelector('.template-cart')) {
      auditCartPage();
    }
    document.addEventListener('cart:refresh', buildRegistry);
    document.addEventListener('cart:updated', scheduleDrawerAudit);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ═══════════════════════════════════════════════════════════════════════
     PUBLIC API
     ═══════════════════════════════════════════════════════════════════════ */
  return {
    preflight:           preflight,
    registerVariant:     registerVariant,
    showToast:           showToast,
    _getRegistry:        function () { return Object.assign({}, _registry); },
    _parseLimitFromTags: parseLimitFromTags,
    _scanDOMForVariant:  _scanDOMForVariant
  };

})();